import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  CheckCircle2, XCircle, AlertTriangle, Loader2, Copy, ArrowLeft,
  Smartphone, MapPin, Volume2, Wifi,
} from 'lucide-react';
import { toast } from 'sonner';

/**
 * /diag — Public diagnostics page for the live audio-tour experience.
 *
 * Use case: at live demos, when a guest's phone "doesn't work," they tap this URL
 * and the page surfaces exactly which web API failed (geolocation permission,
 * audio context state, in-app webview detection, network protocol, etc.) so the
 * host can read it off and know which of the 5 most-common failure modes hit.
 *
 * No tour code touched — pure introspection.
 */
const DiagnosticsPage = () => {
  const [geoState, setGeoState] = useState({ status: 'pending' });
  const [audioState, setAudioState] = useState({ status: 'pending' });
  const watchIdRef = useRef(null);

  // ---- Environment detection (synchronous) ----
  const ua = navigator.userAgent || '';
  const isAndroid = /Android/i.test(ua);
  const isIOS = /iPhone|iPad|iPod/i.test(ua);

  // Match the most common in-app browsers users get stuck in.
  const inAppPatterns = [
    { name: 'Facebook in-app', re: /FBAN|FBAV|FB_IAB/i },
    { name: 'Instagram in-app', re: /Instagram/i },
    { name: 'WhatsApp in-app', re: /WhatsApp/i },
    { name: 'Messenger in-app', re: /Messenger/i },
    { name: 'TikTok in-app', re: /BytedanceWebview|TikTok/i },
    { name: 'LinkedIn in-app', re: /LinkedInApp/i },
    { name: 'WeChat in-app', re: /MicroMessenger/i },
  ];
  const inAppMatch = inAppPatterns.find((p) => p.re.test(ua));

  const protocol = window.location.protocol;
  const isSecure = protocol === 'https:' || window.location.hostname === 'localhost';

  // ---- Geolocation probe ----
  useEffect(() => {
    if (!('geolocation' in navigator)) {
      setGeoState({ status: 'unsupported' });
      return;
    }
    const startWatch = () => {
      const id = navigator.geolocation.watchPosition(
        (pos) => setGeoState({
          status: 'granted',
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: Math.round(pos.coords.accuracy),
          age_ms: Date.now() - pos.timestamp,
        }),
        (err) => {
          const reason = err.code === 1 ? 'Permission denied'
            : err.code === 2 ? 'Position unavailable (no GPS signal)'
            : err.code === 3 ? 'Timeout (GPS slow to respond)'
            : 'Unknown error';
          setGeoState({ status: 'error', code: err.code, message: reason });
        },
        { enableHighAccuracy: true, maximumAge: 2000, timeout: 15000 }
      );
      watchIdRef.current = id;
    };

    // If the Permissions API is available, query first (so we know if it'll prompt).
    if (navigator.permissions?.query) {
      navigator.permissions.query({ name: 'geolocation' })
        .then((p) => {
          setGeoState({ status: 'probing', permission: p.state });
          startWatch();
        })
        .catch(() => startWatch());
    } else {
      startWatch();
    }

    return () => {
      if (watchIdRef.current !== null) {
        try { navigator.geolocation.clearWatch(watchIdRef.current); } catch (_) { /* noop */ }
      }
    };
  }, []);

  // ---- Audio probe — explicit user gesture to unlock the AudioContext ----
  const probeAudio = async () => {
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) {
        setAudioState({ status: 'unsupported' });
        return;
      }
      const ctx = new AC();
      if (ctx.state === 'suspended') {
        await ctx.resume();
      }
      // Play a 100ms silent buffer so we know it can actually emit audio
      const buf = ctx.createBuffer(1, ctx.sampleRate * 0.1, ctx.sampleRate);
      const src = ctx.createBufferSource();
      src.buffer = buf;
      src.connect(ctx.destination);
      src.start();
      setAudioState({
        status: 'unlocked',
        state: ctx.state,
        sampleRate: ctx.sampleRate,
      });
    } catch (e) {
      setAudioState({ status: 'error', message: e?.message || String(e) });
    }
  };

  // ---- Pretty helpers ----
  const Icon = ({ ok }) => ok ? <CheckCircle2 className="w-5 h-5 text-emerald-400" /> : <XCircle className="w-5 h-5 text-rose-400" />;

  const summary = (() => {
    const issues = [];
    if (!isSecure) issues.push('Site not loaded over HTTPS — geolocation will be blocked.');
    if (inAppMatch) issues.push(`Opened inside ${inAppMatch.name} — open in Safari/Chrome instead for reliable GPS & audio.`);
    if (geoState.status === 'error' && geoState.code === 1) issues.push('Location permission denied — enable in Settings → Safari/Chrome → Location.');
    if (geoState.status === 'error' && geoState.code === 2) issues.push('No GPS signal — try moving to open sky away from walls.');
    if (geoState.status === 'error' && geoState.code === 3) issues.push('GPS timed out — try restarting the browser, or toggling airplane mode.');
    if (geoState.status === 'granted' && geoState.accuracy > 50) issues.push(`GPS accuracy is ±${geoState.accuracy}m. Tour radii of 25-35m may be too tight for this location.`);
    return issues;
  })();

  const reportText = () => [
    `Audiostride Diagnostics — ${new Date().toISOString()}`,
    `URL: ${window.location.href}`,
    `Secure (HTTPS): ${isSecure}`,
    `Platform: ${isIOS ? 'iOS' : isAndroid ? 'Android' : 'Other'}`,
    `In-app browser: ${inAppMatch?.name || 'No (regular browser)'}`,
    `User-Agent: ${ua}`,
    '',
    `Geolocation status: ${geoState.status}`,
    geoState.permission ? `Permission state: ${geoState.permission}` : '',
    geoState.lat ? `Coordinates: ${geoState.lat.toFixed(6)}, ${geoState.lng.toFixed(6)} ±${geoState.accuracy}m` : '',
    geoState.message ? `Error: ${geoState.message}` : '',
    '',
    `Audio status: ${audioState.status}`,
    audioState.sampleRate ? `Sample rate: ${audioState.sampleRate} Hz` : '',
    audioState.message ? `Audio error: ${audioState.message}` : '',
    '',
    `Detected issues:`,
    ...(summary.length ? summary.map((s) => `  • ${s}`) : ['  None — everything looks healthy.']),
  ].filter(Boolean).join('\n');

  const copyReport = async () => {
    try {
      await navigator.clipboard.writeText(reportText());
      toast.success('Report copied — paste it into your host\'s chat.');
    } catch {
      toast.error('Clipboard blocked. Take a screenshot instead.');
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white" data-testid="diagnostics-page">
      <header className="sticky top-0 z-10 backdrop-blur-md bg-slate-950/80 border-b border-white/10 px-4 py-3 flex items-center gap-3">
        <Link to="/" className="text-white/70 hover:text-white text-sm font-bold uppercase tracking-wider inline-flex items-center gap-1.5">
          <ArrowLeft className="w-4 h-4" /> Back
        </Link>
        <h1 className="font-black text-lg ml-auto">Diagnostics</h1>
      </header>

      <main className="max-w-2xl mx-auto px-5 py-6 space-y-5">

        {/* Summary card */}
        <section className={`rounded-2xl p-5 border-2 ${summary.length === 0 ? 'border-emerald-400/30 bg-emerald-500/10' : 'border-amber-400/30 bg-amber-500/10'}`}>
          <div className="flex items-start gap-3">
            {summary.length === 0
              ? <CheckCircle2 className="w-6 h-6 text-emerald-400 flex-shrink-0 mt-0.5" />
              : <AlertTriangle className="w-6 h-6 text-amber-400 flex-shrink-0 mt-0.5" />}
            <div className="flex-1 min-w-0">
              <h2 className="font-black text-lg mb-1">
                {summary.length === 0 ? 'All systems look healthy' : `${summary.length} issue${summary.length === 1 ? '' : 's'} detected`}
              </h2>
              {summary.length > 0 && (
                <ul className="space-y-1.5 text-sm text-amber-100" data-testid="diag-issues">
                  {summary.map((s, i) => <li key={i}>• {s}</li>)}
                </ul>
              )}
            </div>
          </div>
        </section>

        {/* Environment */}
        <section className="rounded-2xl bg-white/5 border border-white/10 p-5" data-testid="diag-environment">
          <h3 className="font-black uppercase tracking-wider text-xs text-white/60 mb-3 flex items-center gap-2">
            <Smartphone className="w-4 h-4" /> Environment
          </h3>
          <dl className="space-y-2 text-sm">
            <Row label="Connection" value={isSecure ? 'HTTPS (secure)' : `${protocol} (insecure)`} ok={isSecure} />
            <Row label="Platform" value={isIOS ? 'iOS' : isAndroid ? 'Android' : 'Other / Desktop'} />
            <Row label="Browser context" value={inAppMatch?.name || 'Standard browser'} ok={!inAppMatch} />
          </dl>
        </section>

        {/* Geolocation */}
        <section className="rounded-2xl bg-white/5 border border-white/10 p-5" data-testid="diag-geo">
          <h3 className="font-black uppercase tracking-wider text-xs text-white/60 mb-3 flex items-center gap-2">
            <MapPin className="w-4 h-4" /> Geolocation
          </h3>
          {geoState.status === 'pending' && <Pending msg="Asking for permission…" />}
          {geoState.status === 'probing' && <Pending msg={`Probing GPS (permission: ${geoState.permission || 'unknown'})…`} />}
          {geoState.status === 'unsupported' && <Bad msg="This browser does not support Geolocation." />}
          {geoState.status === 'error' && <Bad msg={geoState.message} />}
          {geoState.status === 'granted' && (
            <dl className="space-y-2 text-sm">
              <Row label="Permission" value="Granted" ok />
              <Row label="Coordinates" value={`${geoState.lat.toFixed(6)}, ${geoState.lng.toFixed(6)}`} />
              <Row label="Accuracy" value={`±${geoState.accuracy} meters`} ok={geoState.accuracy <= 35} />
              <Row label="Fresh as of" value={`${(geoState.age_ms / 1000).toFixed(1)}s ago`} />
            </dl>
          )}
        </section>

        {/* Audio */}
        <section className="rounded-2xl bg-white/5 border border-white/10 p-5" data-testid="diag-audio">
          <h3 className="font-black uppercase tracking-wider text-xs text-white/60 mb-3 flex items-center gap-2">
            <Volume2 className="w-4 h-4" /> Audio
          </h3>
          {audioState.status === 'pending' && (
            <>
              <p className="text-sm text-white/70 mb-3">Tap below to test audio playback (a 100ms silent tone — verifies the browser can unlock its audio engine).</p>
              <button
                type="button"
                onClick={probeAudio}
                data-testid="diag-test-audio-btn"
                className="px-5 py-3 rounded-full bg-white text-slate-950 font-black text-sm uppercase tracking-wider active:scale-95"
              >
                Test audio
              </button>
            </>
          )}
          {audioState.status === 'unlocked' && (
            <dl className="space-y-2 text-sm">
              <Row label="Audio engine" value={`Unlocked (${audioState.state})`} ok />
              <Row label="Sample rate" value={`${audioState.sampleRate} Hz`} />
            </dl>
          )}
          {audioState.status === 'unsupported' && <Bad msg="No AudioContext — this browser cannot play audio." />}
          {audioState.status === 'error' && <Bad msg={audioState.message} />}
        </section>

        {/* Network */}
        <section className="rounded-2xl bg-white/5 border border-white/10 p-5" data-testid="diag-network">
          <h3 className="font-black uppercase tracking-wider text-xs text-white/60 mb-3 flex items-center gap-2">
            <Wifi className="w-4 h-4" /> Network
          </h3>
          <dl className="space-y-2 text-sm">
            <Row label="Online" value={navigator.onLine ? 'Yes' : 'No'} ok={navigator.onLine} />
            <Row label="Effective type" value={navigator.connection?.effectiveType || 'unknown'} />
          </dl>
        </section>

        {/* Copy report */}
        <button
          type="button"
          onClick={copyReport}
          data-testid="diag-copy-report-btn"
          className="w-full py-4 rounded-2xl bg-blue-500 hover:bg-blue-400 text-white font-black uppercase tracking-wider shadow-lg active:scale-[0.99] inline-flex items-center justify-center gap-2"
        >
          <Copy className="w-4 h-4" /> Copy report &amp; send to host
        </button>

        <p className="text-xs text-white/40 text-center pt-2">
          This page only reads device capabilities — it does not collect or transmit anything until you tap "Copy report".
        </p>
      </main>
    </div>
  );
};

const Row = ({ label, value, ok }) => (
  <div className="flex items-center justify-between gap-3">
    <dt className="text-white/60 text-sm">{label}</dt>
    <dd className="font-mono text-sm text-right flex items-center gap-2">
      {ok !== undefined && (ok
        ? <CheckCircle2 className="w-4 h-4 text-emerald-400" />
        : <XCircle className="w-4 h-4 text-rose-400" />)}
      {value}
    </dd>
  </div>
);

const Pending = ({ msg }) => (
  <div className="flex items-center gap-2 text-white/70 text-sm">
    <Loader2 className="w-4 h-4 animate-spin" /> {msg}
  </div>
);

const Bad = ({ msg }) => (
  <div className="flex items-start gap-2 text-rose-200 text-sm">
    <XCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
    <span>{msg}</span>
  </div>
);

export default DiagnosticsPage;
